import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Modal,
} from 'react-native';
import styles from './PeopleActivityScreen.styles';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ActivityType = 'pulse_check' | 'full_observation' | 'routine_observation';

export interface ActivitySession {
  /** Internal identifier for the employee */
  employeeId: string;
  employeeName: string;
  role: string;
  /** "New Hire" | "Tenured" */
  employeeProfile: string;
  managerName: string;
  activityType: ActivityType;
  // ── Fields returned by POST /people/activities/start ─────────────────────
  /** Server-side activity ID created by the start-activity call. */
  activityId?: number | string;
  taskId?: number | string;
  status?: string;
  isAdHoc?: boolean;
  fiscalYear?: number;
  fiscalPeriod?: number;
  fiscalWeek?: number;
  stage?: string;
  /** Checklist selections carried over from POST /people/activities RESUME (itemId -> checked). */
  checklistSelections?: Record<string, boolean>;
  /** Checklist images carried over from POST /people/activities RESUME. */
  checklistImages?: Array<{ imageBase64: string; imageId: string; description: string; checklistItemId: number | string }>;
  /** Custom checklist rows carried over from POST /people/activities RESUME. */
  customChecklist?: Array<{ id: string; checklistItem: string }>;
  /** Balanced Feedback carried over from POST /people/activities RESUME (raw backend field names). */
  resumedFeedback?: Record<string, string | null>;
  /** Pulse Check only — the primary open coaching item's saved follow-up result/feedback. */
  resumedCoachingResult?: string | null;
  resumedSupervisorFeedback?: string | null;
  /** Signatures carried over from POST /people/activities RESUME. */
  resumedSignatures?: Record<string, any>;
  signatures?: Record<string, any>;
  /** True when this session was opened via RESUME from a saved draft. */
  isResumed?: boolean;
  /** True when a COMPLETED activity is opened from Employee Profile > All History —
   *  every input is locked and the footer only offers Exit (plus Back/Next for observations). */
  isReadOnly?: boolean;
}

export interface PeopleActivityScreenProps {
  /** Session data — used to render the title/subtitle */
  session: ActivitySession;
  /** Called by the Exit button when it should just navigate away (no discard, no save) */
  onExit: () => void;
  /** Called instead of onExit when the Exit button should also discard the unsaved activity
   *  (direct-exit path — first stage only). Defaults to onExit when omitted. */
  onDiscardExit?: () => void;
  /** True when the activity is on its first stage — Exit discards directly instead of
   *  showing the Leave Activity modal. Default true (non-observation activities). */
  isFirstStage?: boolean;
  /** Called after a successful completion submission */
  onComplete: () => void;
  /** Called by the Save Draft button (pulse-check mode) or Leave modal "Save Draft and Leave" */
  onSaveDraft: () => void | Promise<void>;
  /** Shows "Saving…" label and disables Save Draft while in-flight */
  isSavingDraft?: boolean;
  /** Whether the Complete button is enabled */
  canComplete: boolean;
  /** Shows "Submitting…" label and disables Complete while in-flight */
  isSubmitting: boolean;
  /** Activity-specific scrollable content rendered in the body */
  children: React.ReactNode;
  /** Optional validation error shown above the footer */
  validationError?: string | null;
  // ── Observation footer mode ──────────────────────────────────────────────
  // When onNext is provided the footer switches to the observation layout
  // (Exit + optional Back left, Next or Complete right; no Save Draft button).
  /** Label for the Back button, e.g. "Back: Before Activity". Omit to hide Back. */
  backLabel?: string;
  /** Called when the Back button is pressed */
  onBack?: () => void;
  /** Called when the Next button is pressed (steps 0-2). When undefined the
   *  right-side button becomes Complete (step 3). */
  onNext?: () => void;
  /** Label for the Next button, e.g. "Next: During Activity". Omit on last step. */
  nextLabel?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<ActivityType, string> = {
  pulse_check:         'Pulse Check',
  full_observation:    'Full Observation',
  routine_observation: 'Routine Observation',
};

const STAGE_LABELS: Record<string, string> = {
  BEFORE_ACTIVITY: 'Before Activity',
  DURING_ACTIVITY: 'During Activity',
  AFTER_ACTIVITY: 'After Activity',
  COACHING: 'Coaching',
};

function formatSubtitle(session: ActivitySession): string {
  const parts: string[] = [session.employeeProfile];
  if (session.role) parts.push(session.role);
  const today = new Date();
  parts.push(
    today.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  );
  return parts.join(' / ');
}

function formatResumedStage(stage: string | undefined): string {
  if (!stage) return 'During Activity';
  const normalized = String(stage).trim().toUpperCase().replace(/\s+/g, '_');
  if (STAGE_LABELS[normalized]) return STAGE_LABELS[normalized];
  if (/^STAGE_?1$|^1$/.test(normalized)) return 'Before Activity';
  if (/^STAGE_?2$|^2$/.test(normalized)) return 'During Activity';
  if (/^STAGE_?3$|^3$/.test(normalized)) return 'After Activity';
  if (/^STAGE_?4$|^4$/.test(normalized)) return 'Coaching';
  return 'During Activity';
}

function getRetainedObservedConditionsCount(
  checklistSelections: ActivitySession['checklistSelections'],
): number {
  if (!checklistSelections) return 0;
  return Object.values(checklistSelections).filter(Boolean).length;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * PeopleActivityScreen
 *
 * Shared full-screen layout for all People Hub activity types
 * (Pulse Check, Full Observation, Routine Observation).
 *
 * Pulse-check footer:  Exit (left)  ·  Save Draft + Complete (right)
 * Observation footer:  Exit + Back? (left)  ·  Next or Complete (right)
 *
 * When the observation footer is active, pressing Exit shows a Leave Activity
 * modal instead of exiting immediately.
 */
const PeopleActivityScreen: React.FC<PeopleActivityScreenProps> = ({
  session,
  onExit,
  onDiscardExit,
  isFirstStage = true,
  onComplete,
  onSaveDraft,
  isSavingDraft = false,
  canComplete,
  isSubmitting,
  children,
  validationError,
  backLabel,
  onBack,
  onNext,
  nextLabel,
}) => {
  const activityLabel = ACTIVITY_LABELS[session.activityType];
  const isReadOnly = session.isReadOnly === true;
  const showResumeCard = session.isResumed === true && !isReadOnly;
  const resumedStageLabel = formatResumedStage(session.stage);
  const retainedObservedConditions = getRetainedObservedConditionsCount(session.checklistSelections);
  // Observation passes onNext only for steps 0-2 (nextLabel defined) — on the final Coaching
  // step onNext is undefined too, so onBack/backLabel are checked as well to keep the
  // observation footer (Back + Complete, Exit via Leave modal) instead of falling back to
  // the pulse-check footer (plain Save Draft button with no save-then-navigate behavior).
  const isObservationMode = onNext !== undefined || onBack !== undefined || backLabel != null;
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isLeaveSaving, setIsLeaveSaving] = useState(false);
  const discardExit = onDiscardExit ?? onExit;

  const handleExitPress = () => {
    if (isObservationMode && !isFirstStage && !isReadOnly) {
      setShowLeaveModal(true);
    } else {
      discardExit();
    }
  };

  return (
    <View style={styles.root}>
      {/* ── Scrollable body ──────────────────────────────────────────────── */}
      <ScrollView
        style={styles.scrollBody}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
        scrollEventThrottle={16}
      >
        {/* Page head — title + subtitle scroll with content */}
        <View style={styles.pagehead}>
          <View style={styles.pageheadText}>
            <Text style={styles.pageTitle}>
              {activityLabel}: {session.employeeName}
            </Text>
            <Text style={styles.pageSubtitle}>{formatSubtitle(session)}</Text>
          </View>
        </View>

        {isReadOnly && (
          <View style={styles.resumeDraftCard}>
            <Text style={styles.resumeDraftText}>
              This activity is completed and is shown in read-only mode.
            </Text>
          </View>
        )}

        {showResumeCard && (
          <View style={styles.resumeDraftCard}>
            <Text style={styles.resumeDraftText}>
              Resumed saved draft at stage {resumedStageLabel} with {retainedObservedConditions} observed conditions retained.
            </Text>
          </View>
        )}

        {/* Activity-specific sections */}
        {children}

        {/* Validation error — rendered inside the scroll body, above the footer */}
        {!!validationError && !isReadOnly && (
          <View style={styles.validationBar}>
            <Text style={styles.validationText}>{validationError}</Text>
          </View>
        )}
      </ScrollView>

      {/* ── Fixed footer ─────────────────────────────────────────────────── */}
      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          {/* Exit — secondary button (blue border + blue text) */}
          <TouchableOpacity
            style={[styles.footerButton, styles.footerButtonSecondary]}
            onPress={handleExitPress}
            accessibilityLabel={`Exit ${activityLabel}`}
          >
            <Text style={[styles.footerButtonText, styles.footerButtonSecondaryText]}>Exit</Text>
          </TouchableOpacity>

          {/* Observation: optional Back button (secondary) */}
          {isObservationMode && backLabel != null && (
            <TouchableOpacity
              style={[styles.footerButton, styles.footerButtonSecondary]}
              onPress={onBack}
              accessibilityLabel={backLabel}
            >
              <Text style={[styles.footerButtonText, styles.footerButtonSecondaryText]}>{backLabel}</Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.footerRight}>
          {isObservationMode ? (
            /* Observation right side: Next (steps 0-2) or Complete (step 3) */
            nextLabel != null ? (
              <TouchableOpacity
                style={[styles.footerButton, styles.footerButtonPrimary]}
                onPress={onNext}
                accessibilityLabel={nextLabel}
              >
                <Text style={[styles.footerButtonText, styles.footerButtonPrimaryText]}>
                  {nextLabel}
                </Text>
              </TouchableOpacity>
            ) : isReadOnly ? null : (
              <TouchableOpacity
                style={[
                  styles.footerButton,
                  styles.footerButtonPrimary,
                  !canComplete && styles.footerButtonPrimaryDisabled,
                ]}
                onPress={onComplete}
                disabled={!canComplete || isSubmitting}
                accessibilityLabel={`Complete ${activityLabel}`}
              >
                <Text
                  style={[
                    styles.footerButtonText,
                    styles.footerButtonPrimaryText,
                    !canComplete && styles.footerButtonPrimaryDisabledText,
                  ]}
                >
                  {isSubmitting ? 'Submitting…' : 'Complete'}
                </Text>
              </TouchableOpacity>
            )
          ) : isReadOnly ? null : (
            /* Pulse-check right side: Save Draft (secondary) + Complete (primary) */
            <>
              <TouchableOpacity
                style={[styles.footerButton, styles.footerButtonSecondary]}
                onPress={onSaveDraft}
                disabled={isSavingDraft}
                accessibilityLabel="Save draft"
              >
                <Text style={[styles.footerButtonText, styles.footerButtonSecondaryText]}>
                  {isSavingDraft ? 'Saving…' : 'Save Draft'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.footerButton,
                  styles.footerButtonPrimary,
                  !canComplete && styles.footerButtonPrimaryDisabled,
                ]}
                onPress={onComplete}
                disabled={!canComplete || isSubmitting}
                accessibilityLabel={`Complete ${activityLabel}`}
              >
                <Text
                  style={[
                    styles.footerButtonText,
                    styles.footerButtonPrimaryText,
                    !canComplete && styles.footerButtonPrimaryDisabledText,
                  ]}
                >
                  {isSubmitting ? 'Submitting…' : 'Complete'}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>

      {/* ── Leave Activity modal (observation mode only) ──────────────────── */}
      <Modal
        visible={showLeaveModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowLeaveModal(false)}
      >
        <View style={styles.leaveModalOverlay}>
          <View style={styles.leaveModalCard}>
            <Text style={styles.leaveModalTitle}>Leave this activity?</Text>
            <Text style={styles.leaveModalBody}>
              Save the current progress as a draft, or leave without saving the changes made in
              this session.
            </Text>
            <View style={styles.leaveModalFooter}>
              <TouchableOpacity
                style={[styles.footerButton, styles.footerButtonSecondary, styles.leaveModalButton]}
                onPress={() => setShowLeaveModal(false)}
                accessibilityLabel="Cancel"
              >
                <Text style={[styles.footerButtonText, styles.footerButtonSecondaryText]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.footerButton, styles.footerButtonSecondary, styles.leaveModalButton]}
                onPress={() => { setShowLeaveModal(false); onExit(); }}
                disabled={isLeaveSaving}
                accessibilityLabel="Leave without saving"
              >
                <Text style={[styles.footerButtonText, styles.footerButtonSecondaryText]}>Leave Without Saving</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.footerButton, styles.footerButtonPrimary, styles.leaveModalButton]}
                onPress={async () => {
                  setIsLeaveSaving(true);
                  try {
                    await onSaveDraft();
                  } finally {
                    setIsLeaveSaving(false);
                  }
                  setShowLeaveModal(false);
                  onExit();
                }}
                disabled={isLeaveSaving}
                accessibilityLabel="Save draft and leave"
              >
                <Text style={[styles.footerButtonText, styles.footerButtonPrimaryText]}>
                  {isLeaveSaving ? 'Saving…' : 'Save Draft and Leave'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default PeopleActivityScreen;
